/**
 * PrLiteratureReviewContext
 *
 * Independent data/caching context for the PR Literature Review section
 * (Data Consolidation group in the main sidebar). It is a dedicated copy of the
 * journal articles context so this section owns its own article list,
 * pagination, filters, view settings, and selection — fully decoupled from the
 * Journal Articles section even though both read the same backend services.
 *
 * Navigating away from a list (e.g. to a detail page) and back does not trigger
 * a full reload: the context caches articles, pagination state, filters, and
 * view settings for the life of the provider.
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  ReactNode,
} from "react"
import {
  JournalArticle,
  getJournalArticles,
  extractionStatusLabelMap,
  topicLabelMap,
  authorContactStatusLabelMap,
} from "@/services/journalArticlesService"
import {
  BiblioCollection,
  getCollections,
  COLLECTION_TYPE_TOPIC_KEY_MAP,
} from "@/services/biblioService"
import { useAuth } from "@/hooks/useAuth"
import { SidebarOption } from "@/components/pr-literature-review/PrLiteratureReviewSidebar"
import { GenericSortConfig } from "@/components/survey-catalogue/SortableColumnHeader"
import { ViewMode } from "@/components/shared/TableViewSelector"
import { getColumnGroupsForViewMode } from "@/components/pr-literature-review/columns"

/** Sortable column key type for PR literature review articles. */
export type JournalArticleSortableKey = "publicationYear" | "scorePRTopic"

/**
 * The PR topic score threshold that the Needs Rereview and New Finds pages pin
 * (scorePRTopic > 0.5). On those pages it is also the lower bound (floor) of the
 * PR score slider; the All PR Articles page has no inherent floor (its slider
 * starts at 0).
 */
export const PR_SCORE_THRESHOLD: number = 0.5

/** Default page size for article pagination. */
const DEFAULT_PAGE_SIZE: number = 50

/**
 * Reserved sentinel id used by the biblio-collections table to store the
 * atomic collection-id counter (its item holds `currentValue`, not a real
 * collection). It must be excluded from the collections shown in the UI.
 */
const COLLECTIONS_COUNTER_ID: number = -1

/**
 * The sidebar options that are "score pages" — PR-classifier-score views that
 * expose the minimum-score slider. Centralised so both the context and the
 * page hook agree on which options show the slider and carry a score filter.
 *   - all-pr-articles: all articles labelled with the PR topic; slider floor 0.
 *   - needs-rereview:  scorePRTopic > 0.5 AND coversPRTopic === false.
 *   - new-finds:       scorePRTopic > 0.5 AND coversPRTopic has no value.
 */
export const SCORE_PAGE_OPTIONS: ReadonlySet<SidebarOption> = new Set<SidebarOption>([
  "all-pr-articles",
  "needs-rereview",
  "new-finds",
])

/**
 * The slider floor for a given sidebar option. Needs Rereview and New Finds pin
 * scorePRTopic > 0.5, so their slider cannot drop below 0.5; every other option
 * (including All PR Articles) has no inherent floor.
 *
 * @param option - The sidebar option to resolve the floor for.
 * @returns The minimum PR score the slider may select for that option.
 */
export function scoreFloorForOption(option: SidebarOption): number {
  return option === "needs-rereview" || option === "new-finds" ? PR_SCORE_THRESHOLD : 0
}

/**
 * Shape of the cached state for PR literature review articles.
 */
interface PrLiteratureReviewCacheState {
  /** The list of articles currently loaded. */
  articles: JournalArticle[]
  /** Total number of articles matching the current filters. */
  totalCount: number
  /** Total number of pages available. */
  totalPages: number
  /** Current page index (0-based). */
  pageIndex: number
  /** Number of items per page. */
  pageSize: number
  /** Current sidebar selection (determines which articles to show). */
  sidebarOption: SidebarOption
  /**
   * Minimum PR topic score selected on the score slider (only meaningful on the
   * score pages: All PR Articles / Needs Rereview / New Finds). Articles are
   * filtered to scorePRTopic strictly greater than this value. 0 means the
   * slider imposes no extra floor.
   */
  prScoreMin: number
  /** Current search query string. */
  searchQuery: string
  /** Active table filters (key → set of selected values). */
  activeFilters: Map<string, Set<string>>
  /** Current sort configuration. */
  sortConfig: GenericSortConfig<JournalArticleSortableKey> | null
  /** Current view mode (compact, status, full). */
  viewMode: ViewMode
  /** Set of visible column keys. */
  visibleColumns: Set<string>
  /** Whether data is currently being fetched. */
  loading: boolean
  /** Error message if the last fetch failed. */
  error: string | null
  /** Timestamp of the last successful fetch (for cache invalidation). */
  lastFetchTime: number | null
}

/**
 * Context value shape with state and actions.
 */
export interface PrLiteratureReviewContextValue extends PrLiteratureReviewCacheState {
  /** List of biblio collections (for the GBD Cycles list and filter dropdowns). */
  collections: BiblioCollection[]
  /** Whether collections are still loading. */
  collectionsLoading: boolean
  /** Currently selected article (for the side panel). */
  selectedArticle: JournalArticle | null
  /** ID of the currently selected article. */
  selectedArticleId: number | null
  /** Fetch a page of articles with the current filters. */
  fetchPage: (page: number) => Promise<void>
  /** Set the current page index. */
  setPageIndex: (index: number) => void
  /** Set the page size. */
  setPageSize: (size: number) => void
  /** Set the sidebar option (triggers refetch). */
  setSidebarOption: (option: SidebarOption) => void
  /** Set the minimum PR topic score from the slider (triggers refetch). */
  setPrScoreMin: (min: number) => void
  /** Set the search query (triggers refetch). */
  setSearchQuery: (query: string) => void
  /** Set the active filters (triggers refetch). */
  setActiveFilters: (filters: Map<string, Set<string>>) => void
  /** Set the sort configuration (triggers refetch). */
  setSortConfig: (config: GenericSortConfig<JournalArticleSortableKey> | null) => void
  /** Toggle sort for a given column key. */
  toggleSort: (key: JournalArticleSortableKey) => void
  /** Set the view mode. */
  setViewMode: (mode: ViewMode) => void
  /** Set the visible columns. */
  setVisibleColumns: (columns: Set<string>) => void
  /** Select an article (shows side panel). */
  selectArticle: (article: JournalArticle) => void
  /** Clear the selected article (closes side panel). */
  clearSelectedArticle: () => void
  /** Whether sorting is enabled for the current sidebar option. */
  sortEnabled: boolean
  /** Force a refresh of the current page (bypasses cache). */
  refreshCurrentPage: () => Promise<void>
  /** Fetch collections from the API. */
  fetchCollections: () => Promise<void>
  /** Update an article in the cached list. */
  updateArticleInList: (updated: JournalArticle) => void
}

const PrLiteratureReviewContext = createContext<PrLiteratureReviewContextValue | null>(null)

/**
 * Props for the PrLiteratureReviewProvider component.
 */
interface PrLiteratureReviewProviderProps {
  /** Child components that can access the context. */
  children: ReactNode
  /** Initial sidebar option (derived from URL path). */
  initialSidebarOption?: SidebarOption
  /** Initial search query (from URL params). */
  initialSearchQuery?: string
  /** Initial page index (from URL params). */
  initialPageIndex?: number
  /** Initial page size (from URL params). */
  initialPageSize?: number
  /** Initial view mode (from URL params). */
  initialViewMode?: ViewMode
  /** Initial visible columns (from URL params). */
  initialVisibleColumns?: Set<string>
  /** Initial active filters (from URL params). */
  initialActiveFilters?: Map<string, Set<string>>
  /** Initial minimum PR topic score for the slider (from URL params). */
  initialPrScoreMin?: number
}

/**
 * Provider component that manages PR literature review state and caching.
 *
 * The default sidebar option is the overview, since the section lands on the
 * Overview page.
 *
 * @param props - Provider props including children and initial state values.
 * @returns The provider component wrapping children.
 */
export function PrLiteratureReviewProvider({
  children,
  initialSidebarOption = "overview",
  initialSearchQuery = "",
  initialPageIndex = 0,
  initialPageSize = DEFAULT_PAGE_SIZE,
  initialViewMode = "compact",
  initialVisibleColumns,
  initialActiveFilters,
  initialPrScoreMin = 0,
}: PrLiteratureReviewProviderProps): React.JSX.Element {
  const { getAccessToken } = useAuth()

  // Core data state
  const [articles, setArticles] = useState<JournalArticle[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [totalPages, setTotalPages] = useState<number>(1)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [lastFetchTime, setLastFetchTime] = useState<number | null>(null)

  // Pagination state
  const [pageIndex, setPageIndex] = useState<number>(initialPageIndex)
  const [pageSize, setPageSize] = useState<number>(initialPageSize)

  // Filter and view state
  const [sidebarOption, setSidebarOptionState] = useState<SidebarOption>(initialSidebarOption)
  const [prScoreMin, setPrScoreMinState] = useState<number>(initialPrScoreMin)
  const [searchQuery, setSearchQueryState] = useState<string>(initialSearchQuery)
  const [activeFilters, setActiveFiltersState] = useState<Map<string, Set<string>>>(
    initialActiveFilters ?? new Map()
  )
  const [sortConfig, setSortConfigState] =
    useState<GenericSortConfig<JournalArticleSortableKey> | null>(null)
  const [viewMode, setViewModeState] = useState<ViewMode>(initialViewMode)
  const [visibleColumns, setVisibleColumnsState] = useState<Set<string>>(
    initialVisibleColumns ??
      new Set(getColumnGroupsForViewMode(initialViewMode).flatMap((g) => g.keys))
  )

  // Selection state
  const [selectedArticle, setSelectedArticle] = useState<JournalArticle | null>(null)
  const [selectedArticleId, setSelectedArticleId] = useState<number | null>(null)

  // Mirror of selectedArticleId kept in a ref so fetchPage can read the latest
  // value WITHOUT listing it as a dependency. If fetchPage depended on
  // selectedArticleId, selecting an article (which sets the id) would recreate
  // fetchPage, and the page's fetch effect — which depends on fetchPage — would
  // re-run and reload the whole list. The ref breaks that chain.
  const selectedArticleIdRef = useRef<number | null>(null)
  useEffect(() => {
    selectedArticleIdRef.current = selectedArticleId
  }, [selectedArticleId])

  // Collections state
  const [collections, setCollections] = useState<BiblioCollection[]>([])
  const [collectionsLoading, setCollectionsLoading] = useState<boolean>(true)

  // Sorting is disabled for the overview option (no table) and otherwise
  // enabled. The overview never drives fetchPage, so this is a conservative
  // default mirroring the journal articles behaviour for non-"all" views.
  const sortEnabled: boolean = sidebarOption !== "overview"

  // Fetch ID ref to discard stale responses
  const fetchIdRef = useRef<number>(0)

  /**
   * Reverse-lookup a label map to find API keys from selected labels.
   *
   * @param selectedLabels - Set of human-readable labels the user selected.
   * @param labelMap - The label map (API value → display label) to reverse.
   * @returns Comma-separated API values, or undefined if nothing selected.
   */
  const resolveFilterValues = useCallback(
    (
      selectedLabels: Set<string> | undefined,
      labelMap: Record<string, string>
    ): string | undefined => {
      if (!selectedLabels?.size) return undefined
      const reversed = Object.entries(labelMap).reduce<Record<string, string>>(
        (acc, [key, label]) => {
          acc[label] = key
          return acc
        },
        {}
      )
      const apiValues = [...selectedLabels].map((label) => reversed[label]).filter(Boolean)
      return apiValues.length > 0 ? apiValues.join(",") : undefined
    },
    []
  )

  /**
   * Join a raw filter value set into a comma-separated string for the API.
   *
   * Unlike {@link resolveFilterValues}, this performs no label-map reversal: the
   * selected values are already the exact strings the backend expects (e.g.
   * continent names, country names, or study years). Used for the
   * continents/countries/studyYears filters on the Open Author Contacts view.
   *
   * @param selectedValues - Set of raw values the user selected, or undefined.
   * @returns Comma-separated values, or undefined when nothing is selected.
   */
  const joinRawFilterValues = useCallback(
    (selectedValues: Set<string> | undefined): string | undefined => {
      if (!selectedValues?.size) return undefined
      return [...selectedValues].join(",")
    },
    []
  )

  /**
   * Resolve a collection filter label set back to a comma-separated list of
   * collection IDs. Multiple collections can be selected; the backend unions
   * their members (OR logic), so all selected ids are forwarded.
   *
   * @param selectedLabels - Set of collection name labels the user selected.
   * @returns Comma-separated collection IDs, or undefined when none selected.
   */
  const resolveCollectionIds = useCallback(
    (selectedLabels: Set<string> | undefined): string | undefined => {
      if (!selectedLabels?.size) return undefined
      const ids = collections
        .filter((c) => selectedLabels.has(c.collectionName))
        .map((c) => c.id)
      return ids.length > 0 ? ids.join(",") : undefined
    },
    [collections]
  )

  /**
   * Fetch a page of articles from the API.
   *
   * @param page - Zero-based page index to fetch.
   */
  const fetchPage = useCallback(
    async (page: number): Promise<void> => {
      const currentFetchId = ++fetchIdRef.current

      setLoading(true)
      setError(null)

      try {
        const token = await getAccessToken()

        // Build filters from sidebar selection. GBD cycle options (collection
        // options) pin the "pr" topic via their collection type.
        const collectionMatch = sidebarOption.match(/^collection-(\d+)$/)
        const selectedCollectionId = collectionMatch
          ? parseInt(collectionMatch[1], 10)
          : undefined

        // When a cycle is selected, restrict its articles to the topic that
        // matches the collection's type (PR cycles → coversPRTopic = true).
        const selectedCollection =
          selectedCollectionId !== undefined
            ? collections.find((collection) => collection.id === selectedCollectionId)
            : undefined
        const collectionTopicKey = selectedCollection
          ? COLLECTION_TYPE_TOPIC_KEY_MAP[selectedCollection.collectionType]
          : undefined

        // Score pages (All PR Articles / Needs Rereview / New Finds) expose the
        // minimum-score slider. Needs Rereview and New Finds additionally pin
        // scorePRTopic > 0.5, so the effective lower bound is the larger of that
        // floor and the slider's selected minimum. A value of 0 is omitted so a
        // page at its floor imposes no score threshold. These two pages narrow
        // further by the human PR-coverage flag (false vs. missing). All PR
        // Articles instead simply pins the PR topic (articles labelled PR).
        const isScorePage: boolean = SCORE_PAGE_OPTIONS.has(sidebarOption)
        const tabScoreFloor: number = scoreFloorForOption(sidebarOption)
        const effectiveScoreMin: number = Math.max(tabScoreFloor, prScoreMin)

        const scorePageFilters: Pick<
          Parameters<typeof getJournalArticles>[3] & object,
          "scorePRTopicExists" | "scorePRTopicGt" | "coversPRTopicIs"
        > =
          sidebarOption === "needs-rereview"
            ? {
                scorePRTopicExists: true,
                ...(effectiveScoreMin > 0 ? { scorePRTopicGt: effectiveScoreMin } : {}),
                coversPRTopicIs: "false" as const,
              }
            : sidebarOption === "new-finds"
              ? {
                  scorePRTopicExists: true,
                  ...(effectiveScoreMin > 0 ? { scorePRTopicGt: effectiveScoreMin } : {}),
                  coversPRTopicIs: "missing" as const,
                }
              : sidebarOption === "all-pr-articles"
                ? {
                    // All PR Articles is topic-based (see `topic` below). The
                    // slider is still offered, so forward a score floor when the
                    // user raises it above 0.
                    ...(effectiveScoreMin > 0 ? { scorePRTopicGt: effectiveScoreMin } : {}),
                  }
                : {}

        const data = await getJournalArticles(token, page + 1, pageSize, {
          topic:
            collectionTopicKey ??
            // All PR Articles and Open Author Contacts are PR-topic views.
            (sidebarOption === "all-pr-articles" || sidebarOption === "open-author-contacts"
              ? "pr"
              : undefined),
          ...scorePageFilters,
          collectionId: selectedCollectionId,
          searchTerm: searchQuery || undefined,
          prExtractionStatus:
            sidebarOption === "open-author-contacts"
              ? "IN_PROGRESS,EXTRACTED,EDITING,EXTRACTED_VIA_COMMS"
              : resolveFilterValues(
                  activeFilters.get("prExtractionStatus"),
                  extractionStatusLabelMap
                ),
          prUploadStatus:
            sidebarOption === "open-author-contacts" ? "NONE,PENDING_UPLOAD" : undefined,
          // Only fall back to the user-selected topic filter on views that do
          // not already pin a topic (i.e. not cycle / All PR Articles / Open
          // Author Contacts / the score pages, which are all PR-topic views).
          ...(!collectionTopicKey &&
          !isScorePage &&
          sidebarOption !== "open-author-contacts"
            ? { topic: resolveFilterValues(activeFilters.get("topics"), topicLabelMap) }
            : {}),
          ...(!collectionMatch
            ? { collectionId: resolveCollectionIds(activeFilters.get("collectionIds")) }
            : {}),
          rsExtractionStatus: resolveFilterValues(
            activeFilters.get("rsExtractionStatus"),
            extractionStatusLabelMap
          ),
          prContactStatus: resolveFilterValues(
            activeFilters.get("prContactStatus"),
            authorContactStatusLabelMap
          ),
          // Continents / Countries / Study Years are only offered on the Open
          // Author Contacts view, so they are only forwarded there. Their
          // selected values are the exact strings the backend expects, so no
          // label-map reversal is applied — they are joined verbatim.
          ...(sidebarOption === "open-author-contacts"
            ? {
                continents: joinRawFilterValues(activeFilters.get("continents")),
                countries: joinRawFilterValues(activeFilters.get("countries")),
                studyYears: joinRawFilterValues(activeFilters.get("studyYears")),
              }
            : {}),
          sortBy: sortConfig?.key ?? undefined,
          sortDirection: sortConfig?.direction ?? undefined,
        })

        // Discard stale responses
        if (currentFetchId !== fetchIdRef.current) {
          return
        }

        setArticles(data.items)
        setTotalCount(data.totalCount)
        setTotalPages(data.totalPages)
        setLastFetchTime(Date.now())

        // If a selected article ID exists, try to resolve it from the loaded
        // data. Read from the ref (not the state value) so fetchPage does not
        // need selectedArticleId as a dependency — see the ref declaration above.
        const currentSelectedId = selectedArticleIdRef.current
        if (currentSelectedId !== null) {
          const match = data.items.find((a) => a.sourceId === currentSelectedId)
          setSelectedArticle(match ?? null)
        }
      } catch (err) {
        if (currentFetchId !== fetchIdRef.current) {
          return
        }
        setError(err instanceof Error ? err.message : "Failed to load PR literature review articles")
      } finally {
        if (currentFetchId === fetchIdRef.current) {
          setLoading(false)
        }
      }
    },
    [
      getAccessToken,
      sidebarOption,
      prScoreMin,
      searchQuery,
      pageSize,
      activeFilters,
      sortConfig,
      resolveFilterValues,
      joinRawFilterValues,
      resolveCollectionIds,
      collections,
    ]
  )

  /**
   * Fetch collections from the API (called once on mount).
   */
  const fetchCollections = useCallback(async (): Promise<void> => {
    try {
      const token = await getAccessToken()
      const data = await getCollections(token)
      // Exclude the reserved atomic-counter row (id = -1); it is not a real
      // collection and has no collectionName, so it must never reach the UI.
      const realCollections = data.items.filter(
        (collection) => collection.id !== COLLECTIONS_COUNTER_ID
      )
      setCollections(realCollections)
    } catch (err) {
      console.error("Failed to fetch collections:", err)
    } finally {
      setCollectionsLoading(false)
    }
  }, [getAccessToken])

  // Load collections on mount
  useEffect(() => {
    fetchCollections()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Set the sidebar option and reset to first page.
   *
   * Switching option also resets the score slider to the new option's floor so
   * a threshold dragged on one score page never carries across to another (and
   * non-score pages reset it to 0).
   *
   * @param option - The new sidebar option.
   */
  const setSidebarOption = useCallback((option: SidebarOption): void => {
    setSidebarOptionState(option)
    setPrScoreMinState(scoreFloorForOption(option))
    setPageIndex(0)
    setSelectedArticle(null)
    setSelectedArticleId(null)
    setSearchQueryState("")
  }, [])

  /**
   * Set the minimum PR topic score (from the slider) and reset to first page.
   *
   * @param min - The new minimum PR topic score (0 means no slider floor).
   */
  const setPrScoreMin = useCallback((min: number): void => {
    setPrScoreMinState(min)
    setPageIndex(0)
    setSelectedArticle(null)
    setSelectedArticleId(null)
  }, [])

  /**
   * Set the search query and reset to first page.
   *
   * @param query - The new search query.
   */
  const setSearchQuery = useCallback((query: string): void => {
    setSearchQueryState(query)
    setPageIndex(0)
    setSelectedArticle(null)
    setSelectedArticleId(null)
  }, [])

  /**
   * Set the active filters and reset to first page.
   *
   * @param filters - The new filter map.
   */
  const setActiveFilters = useCallback((filters: Map<string, Set<string>>): void => {
    setActiveFiltersState(filters)
    setPageIndex(0)
    setSelectedArticle(null)
    setSelectedArticleId(null)
  }, [])

  /**
   * Set the sort configuration and reset to first page.
   *
   * @param config - The new sort config or null to clear.
   */
  const setSortConfig = useCallback(
    (config: GenericSortConfig<JournalArticleSortableKey> | null): void => {
      setSortConfigState(config)
      setPageIndex(0)
    },
    []
  )

  /**
   * Toggle sort for a given column key.
   * Cycles: asc → desc → no sort (for same column), or starts at asc for new
   * column.
   *
   * @param key - The column key to sort by.
   */
  const toggleSort = useCallback((key: JournalArticleSortableKey): void => {
    setSortConfigState((prev): GenericSortConfig<JournalArticleSortableKey> | null => {
      if (prev?.key !== key) {
        return { key, direction: "asc" }
      }
      if (prev.direction === "asc") {
        return { key, direction: "desc" }
      }
      return null
    })
    setPageIndex(0)
  }, [])

  /**
   * Set the view mode.
   *
   * @param mode - The new view mode.
   */
  const setViewMode = useCallback((mode: ViewMode): void => {
    setViewModeState(mode)
  }, [])

  /**
   * Set the visible columns.
   *
   * @param columns - The new set of visible column keys.
   */
  const setVisibleColumns = useCallback((columns: Set<string>): void => {
    setVisibleColumnsState(columns)
  }, [])

  /**
   * Select an article to view in the side panel.
   *
   * @param article - The article to select.
   */
  const selectArticle = useCallback((article: JournalArticle): void => {
    setSelectedArticle(article)
    setSelectedArticleId(article.sourceId)
  }, [])

  /**
   * Clear the selected article (closes the side panel).
   */
  const clearSelectedArticle = useCallback((): void => {
    setSelectedArticle(null)
    setSelectedArticleId(null)
  }, [])

  /**
   * Force a refresh of the current page.
   */
  const refreshCurrentPage = useCallback(async (): Promise<void> => {
    await fetchPage(pageIndex)
  }, [fetchPage, pageIndex])

  /**
   * Update an article in the cached list.
   *
   * @param updated - The updated article object.
   */
  const updateArticleInList = useCallback(
    (updated: JournalArticle): void => {
      setArticles((prev) => prev.map((a) => (a.sourceId === updated.sourceId ? updated : a)))
      // Also update selected article if it's the same one
      if (selectedArticleId === updated.sourceId) {
        setSelectedArticle(updated)
      }
    },
    [selectedArticleId]
  )

  const value: PrLiteratureReviewContextValue = {
    // State
    articles,
    totalCount,
    totalPages,
    pageIndex,
    pageSize,
    sidebarOption,
    prScoreMin,
    searchQuery,
    activeFilters,
    sortConfig,
    viewMode,
    visibleColumns,
    loading,
    error,
    lastFetchTime,
    collections,
    collectionsLoading,
    selectedArticle,
    selectedArticleId,
    sortEnabled,
    // Actions
    fetchPage,
    setPageIndex,
    setPageSize,
    setSidebarOption,
    setPrScoreMin,
    setSearchQuery,
    setActiveFilters,
    setSortConfig,
    toggleSort,
    setViewMode,
    setVisibleColumns,
    selectArticle,
    clearSelectedArticle,
    refreshCurrentPage,
    fetchCollections,
    updateArticleInList,
  }

  return (
    <PrLiteratureReviewContext.Provider value={value}>
      {children}
    </PrLiteratureReviewContext.Provider>
  )
}

/**
 * Hook to access the PR literature review context.
 *
 * @returns The PR literature review context value.
 * @throws Error if used outside of a PrLiteratureReviewProvider.
 */
export function usePrLiteratureReview(): PrLiteratureReviewContextValue {
  const context = useContext(PrLiteratureReviewContext)
  if (!context) {
    throw new Error("usePrLiteratureReview must be used within a PrLiteratureReviewProvider")
  }
  return context
}
